<template>
    <a-card :bordered="false" class="grp-card">
        <a-table
            size="small"
            :columns="columns"
            :data-source="providers"
            row-key="key"
            :loading="loading"
            :expandedRowKeys="expandedKeys"
            :locale="{ emptyText: 'Nenhum dado para exibir.' }"
            @expand="onExpand"
        >
            <template slot="fornecedor" slot-scope="text, provider">
                <div class="grp-sup">
                    <span class="grp-n">{{ provider.name }}</span>
                    <span class="grp-v" v-if="provider.seller_name">{{ provider.seller_name }}</span>
                </div>
            </template>

            <template slot="telefone" slot-scope="text, provider">
                {{ provider.seller_phone || "—" }}
            </template>

            <template slot="valor" slot-scope="text, provider">
                {{ total_provider(provider) | currency }}
            </template>

            <template slot="expandedRowRender" slot-scope="provider">
                <div class="grp-expanded">
                    <div class="grp-store-totals" v-if="stores.length">
                        <div class="grp-store-chip" v-for="store in stores" :key="store.id">
                            <span class="grp-store-name">{{ store.name }}</span>
                            <span class="grp-store-value">{{ total_store(provider, store.id) | currency }}</span>
                        </div>
                    </div>

                    <a-table
                        :columns="getColumns()"
                        :data-source="provider.products"
                        row-key="id"
                        size="small"
                        :pagination="false"
                        :scroll="{ x: getTableWidth() }"
                        class="grp-products-table"
                    >
                        <template slot="produto" slot-scope="text, product">
                            <div class="pro-name">
                                {{ product.name }}
                                <span class="brand" v-if="product.brand">{{ product.brand }}</span>
                                <a-tooltip v-if="product.observations">
                                    <template slot="title">{{ product.observations.toUpperCase() }}</template>
                                    <a-icon type="info-circle" />
                                </a-tooltip>
                            </div>
                        </template>

                        <template slot="offer_price" slot-scope="text, product">
                            <span
                                :class="{
                                    lower: product.offer_price < product.last_purchase,
                                    higher: product.offer_price > product.last_purchase,
                                }"
                            >
                                {{ product.offer_price | currency }}
                            </span>
                        </template>

                        <template slot="last_purchase" slot-scope="text, product">
                            {{ product.last_purchase | currency }}
                        </template>

                        <template slot="quantidade" slot-scope="text, product">
                            {{ quantity_sum(product.stores) }}
                        </template>

                        <template slot="embalagem" slot-scope="text, product">
                            {{
                                product.purchase_unity ||
                                `${product.packing.split(" ")[0]} ${product.packing.split(" ")[1]}`
                            }}
                        </template>

                        <template slot="total" slot-scope="text, product">
                            {{ total(product.stores) | currency }}
                        </template>

                        <template
                            v-for="store in stores"
                            :slot="'store_' + store.id"
                            slot-scope="text, product"
                        >
                            {{ filter_product(product, store.id).requested_quantity || 0 }}
                        </template>
                    </a-table>
                </div>
            </template>
        </a-table>
    </a-card>
</template>

<script>
import { mapState } from "vuex";

export default {
    name: "GeneralReportTab",
    props: {
        data: { type: Array, default: () => [] },
    },
    data() {
        return {
            loading: false,
            expandedKeys: [],
            providers: [],
            stores: [],
            products: [],
        };
    },
    computed: {
        ...mapState(["user"]),
        columns() {
            return [
                { title: "Fornecedor", key: "fornecedor", scopedSlots: { customRender: "fornecedor" } },
                { title: "Telefone", key: "telefone", width: 140, scopedSlots: { customRender: "telefone" } },
                { title: "Valor total", key: "valor", width: 130, align: "right", scopedSlots: { customRender: "valor" } },
            ];
        },
    },
    methods: {
        onExpand(expanded, record) {
            const index = this.expandedKeys.indexOf(record.key);
            if (expanded) {
                if (index === -1) this.expandedKeys.push(record.key);
            } else if (index !== -1) {
                this.expandedKeys.splice(index, 1);
            }
        },
        getTableWidth() {
            return 650 + this.stores.length * 90;
        },
        getColumns() {
            const columns = [
                { title: "Produto", key: "produto", width: 220, scopedSlots: { customRender: "produto" } },
                { title: "Valor", key: "offer_price", width: 90, align: "center", scopedSlots: { customRender: "offer_price" } },
                { title: "Últ. Compra", key: "last_purchase", width: 100, align: "center", scopedSlots: { customRender: "last_purchase" } },
                { title: "Qtd", key: "quantidade", width: 70, align: "center", scopedSlots: { customRender: "quantidade" } },
                { title: "Emb", key: "embalagem", width: 80, align: "center", scopedSlots: { customRender: "embalagem" } },
                { title: "Total", key: "total", width: 100, align: "center", scopedSlots: { customRender: "total" } },
            ];

            this.stores.forEach((store) => {
                columns.push({
                    title: store.name,
                    key: `store_${store.id}`,
                    width: 80,
                    align: "center",
                    scopedSlots: { customRender: `store_${store.id}` },
                });
            });

            return columns;
        },
        filter_product(product, store_id) {
            return (product.stores || []).find((s) => s.store_id == store_id) || {};
        },
        quantity_sum(stores) {
            return stores.reduce((sum, store) => sum + (Number(store.requested_quantity) || 0), 0);
        },
        total(stores) {
            return stores.reduce((sum, store) => {
                const value =
                    store.is_generic == 0 && store.main_product_id == null && this.user.request_type != 1
                        ? parseInt(store.packing.split(" ")[1]) * store.offer_price
                        : store.offer_price;
                return sum + (store.requested_quantity || 0) * value;
            }, 0);
        },
        total_provider(provider) {
            return provider.products.reduce((sum, p) => sum + this.total(p.stores), 0);
        },
        total_store(provider, store_id) {
            let result = 0;
            provider.products.forEach((p) => {
                p.stores.forEach((pp) => {
                    if (pp.store_id == store_id) {
                        const value =
                            pp.is_generic && pp.main_product_id == null && this.user.request_type != 1
                                ? parseInt(pp.packing.split(" ")[1]) * pp.offer_price
                                : pp.offer_price;
                        result += value * pp.requested_quantity;
                    }
                });
            });
            return result;
        },
        process_available_providers() {
            this.products.forEach((pp) => {
                let prov = this.providers.find(
                    (prov) => prov.provider_id == pp.provider_id && pp.seller_id == prov.seller_id
                );
                if (prov) {
                    let pro = prov.products.find((pr) => pr.id == pp.id);
                    if (pro) {
                        pro.stores.push(pp);
                    } else {
                        prov.products.push({ ...pp, stores: [pp] });
                    }
                } else {
                    this.providers.push({
                        key: `${pp.provider_id}-${pp.seller_id}`,
                        provider_id: pp.provider_id,
                        seller_id: pp.seller_id,
                        name: pp.provider_name,
                        seller_name: pp.seller_name,
                        seller_phone: pp.seller_phone,
                        products: [{ ...pp, stores: [pp] }],
                    });
                }
            });
        },
        sort_products() {
            this.providers.forEach((p) => {
                p.products.sort((a, b) => {
                    if (a.name > b.name) return 1;
                    if (b.name > a.name) return -1;
                    return 0;
                });
            });
        },
        sort_stores() {
            this.stores.sort((a, b) => {
                if (a.position > b.position) return 1;
                if (b.position > a.position) return -1;
                return 0;
            });
        },
        async build() {
            this.loading = true;
            this.providers = [];
            this.products = this.data || [];
            this.stores = await this.$store.dispatch("user/fetch_stores");
            this.sort_stores();
            this.process_available_providers();
            this.sort_products();
            this.loading = false;
        },
    },
    watch: {
        data: {
            immediate: true,
            handler() {
                this.build();
            },
        },
    },
};
</script>

<style lang="scss" scoped>
.grp-card {
    --pri: var(--primary-color);
    width: 100%;

    ::v-deep(.ant-card-body) {
        width: 100%;
        padding: 0;
    }
}
.grp-sup {
    display: flex;
    flex-direction: column;
    gap: 2px;
}
.grp-n {
    font-weight: 600;
    color: #262626;
}
.grp-v {
    font-size: 12px;
    color: #8c8c8c;
    font-style: italic;
}

.grp-expanded {
    width: 100%;
    max-width: 100%;
    min-width: 0;
}

.grp-store-totals {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    padding: 0 0 12px;
}
.grp-store-chip {
    display: flex;
    flex-direction: column;
    align-items: center;
    min-width: 80px;
    padding: 4px 10px;
    border-radius: 6px;
    background: #fafafa;
    border: 1px solid #f0f0f0;
}
.grp-store-name {
    font-size: 11px;
    color: #8c8c8c;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 90px;
}
.grp-store-value {
    font-size: 13px;
    font-weight: 600;
    color: var(--pri);
}

::v-deep(.grp-products-table .ant-table-content) {
    overflow-x: auto;
}

.pro-name {
    font-weight: 600;
    font-size: 13px;
    color: #262626;

    .brand {
        margin-left: 6px;
        font-weight: 400;
        color: var(--pri);
    }
}
.lower {
    color: #389e0d;
    font-weight: 600;
}
.higher {
    color: #cf1322;
    font-weight: 600;
}

::v-deep(.ant-table-expanded-row > td) {
    padding: 12px 16px !important;
}
</style>
